import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { BrandingProvider } from "@/contexts/BrandingContext";
import { I18nProvider } from "@/lib/i18n";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import RegisterAgencyPage from "@/pages/RegisterAgencyPage";
import RegisterClientPage from "@/pages/RegisterClientPage";
import QuoteRecoveryPage from "@/pages/QuoteRecoveryPage";
import AdminDashboard from "@/pages/AdminDashboard";
import DriverDashboard from "@/pages/DriverDashboard";
import DriverManagement from "@/pages/DriverManagement";
import AgencyDashboard from "@/pages/AgencyDashboard";
import AgencyManagement from "@/pages/AgencyManagement";
import RideRequestsAdmin from "@/pages/RideRequestsAdmin";
import ProfilePage from "@/pages/ProfilePage";
import CompanyLanding from "@/pages/CompanyLanding";
import CompanyRedirect from "@/pages/CompanyRedirect";
import CompanySettings from "@/pages/CompanySettings";
import PricingRoutes from "@/pages/PricingRoutes";
import Payments from "@/pages/Payments";
import AdminBusinessDashboard from "@/pages/AdminBusinessDashboard";
import AdminClientDiagnostic from "@/pages/AdminClientDiagnostic";
import PublicRequestPage from "@/pages/PublicRequestPage";
import LandingPage from "@/pages/LandingPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AdminDriverCalendar from "@/pages/AdminDriverCalendar";
import SuperDashboard from "@/pages/SuperDashboard";

// Phase 27 — multi-tenant roles. super_admin lands on the SaaS control
// panel (`/super`); company_admin + legacy admin keep `/admin`.
const roleHome = (role) => (
  role === "super_admin" ? "/super"
  : (role === "admin" || role === "company_admin") ? "/admin"
  : role === "agency" ? "/agency"
  : "/driver"
);

const ADMIN_ROLES = ["admin", "company_admin", "super_admin"];
function roleMatches(userRole, required) {
  if (!required) return true;
  if (required === "admin") return ADMIN_ROLES.includes(userRole);
  return userRole === required;
}

function ProtectedRoute({ children, requiredRole }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  if (!user) return <Navigate to="/" replace />;
  if (requiredRole && !roleMatches(user.role, requiredRole)) return <Navigate to={roleHome(user.role)} replace />;
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  return (
    <Routes>
      <Route path="/" element={user ? <Navigate to={roleHome(user.role)} replace /> : <LandingPage />} />
      <Route path="/login" element={user ? <Navigate to={roleHome(user.role)} replace /> : <LoginPage />} />
      <Route path="/forgot-password" element={user ? <Navigate to={roleHome(user.role)} replace /> : <ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/register" element={user ? <Navigate to={roleHome(user.role)} replace /> : <RegisterPage />} />
      <Route path="/register-agency" element={user ? <Navigate to={roleHome(user.role)} replace /> : <RegisterAgencyPage />} />
      <Route path="/register-client" element={user ? <Navigate to={roleHome(user.role)} replace /> : <RegisterClientPage />} />
      <Route path="/quote-recovery" element={user ? <Navigate to={roleHome(user.role)} replace /> : <QuoteRecoveryPage />} />
      <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/drivers" element={<ProtectedRoute requiredRole="admin"><DriverManagement /></ProtectedRoute>} />
      <Route path="/admin/agencies" element={<ProtectedRoute requiredRole="admin"><AgencyManagement /></ProtectedRoute>} />
      <Route path="/admin/requests" element={<ProtectedRoute requiredRole="admin"><RideRequestsAdmin /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute requiredRole="admin"><CompanySettings /></ProtectedRoute>} />
      <Route path="/admin/pricing" element={<ProtectedRoute requiredRole="admin"><PricingRoutes /></ProtectedRoute>} />
      <Route path="/admin/payments" element={<ProtectedRoute requiredRole="admin"><Payments /></ProtectedRoute>} />
      <Route path="/admin/business" element={<ProtectedRoute requiredRole="admin"><AdminBusinessDashboard /></ProtectedRoute>} />
      <Route path="/admin/client-diagnostic" element={<ProtectedRoute requiredRole="admin"><AdminClientDiagnostic /></ProtectedRoute>} />
      <Route path="/admin/drivers/:driverId/calendar" element={<ProtectedRoute requiredRole="admin"><AdminDriverCalendar /></ProtectedRoute>} />
      <Route path="/super" element={<ProtectedRoute requiredRole="super_admin"><SuperDashboard /></ProtectedRoute>} />
      <Route path="/request" element={<PublicRequestPage />} />
      <Route path="/driver" element={<ProtectedRoute requiredRole="driver"><DriverDashboard /></ProtectedRoute>} />
      <Route path="/agency" element={<ProtectedRoute requiredRole="agency"><AgencyDashboard /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      {/* Phase 27 — branded entry pages. `:slug` MUST be last so it doesn't
          swallow earlier named routes. The sub-routes forward to the
          existing centralized flows with ?company=<slug>. */}
      <Route path="/:slug/booking" element={<CompanyRedirect target="booking" />} />
      <Route path="/:slug/client"  element={<CompanyRedirect target="client" />} />
      <Route path="/:slug/driver"  element={<CompanyRedirect target="driver" />} />
      <Route path="/:slug/login"   element={<CompanyRedirect target="login" />} />
      <Route path="/:slug"         element={<CompanyLanding />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <I18nProvider>
          <AuthProvider>
            <BrandingProvider>
              <AppRoutes />
              <Toaster position="top-right" richColors theme="dark" />
            </BrandingProvider>
          </AuthProvider>
        </I18nProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
